// src/services/api/hallPrices.api.ts
import { apiRequest } from "./client";
import {
    HallPriceCategoryItem,
    HallSeatPriceItem,
    HallSeatPriceBulkItem,
} from "../../services/types/hallPrices.types";


// GET /hall-prices?tour_city_id=...
export const getHallPriceCategoriesRequest = async (
    tourCityId: string,
): Promise<HallPriceCategoryItem[]> => {
    const query = new URLSearchParams();
    query.set("tour_city_id", tourCityId);

    return apiRequest<HallPriceCategoryItem[]>(`/hall-prices?${query.toString()}`, {
        method: "GET",
        auth: true,
    });
};

// POST /hall-prices
export const createHallPriceCategoryRequest = async (payload: {
    tour_city_id: string;
    price: number;
    color: string;
}): Promise<HallPriceCategoryItem> => {
    return apiRequest<HallPriceCategoryItem>("/hall-prices", {
        method: "POST",
        auth: true,
        body: JSON.stringify(payload),
        headers: {
            "Content-Type": "application/json",
        },
    });
};

export const updateHallPriceCategoryRequest = async (
    hallPriceCategoryId: string,
    payload: { color: string },
): Promise<HallPriceCategoryItem> => {
    return apiRequest<HallPriceCategoryItem>(`/hall-prices/${hallPriceCategoryId}`, {
        method: "PATCH",
        auth: true,
        body: JSON.stringify(payload),
        headers: {
            "Content-Type": "application/json",
        },
    });
};

// DELETE /hall-prices/{price_id}
export const deleteHallPriceCategoryRequest = async (
    priceId: string,
): Promise<void> => {
    await apiRequest<void>(`/hall-prices/${priceId}`, {
        method: "DELETE",
        auth: true,
    });
};

// GET /hall-prices/seat-prices?tour_city_id=...
export const getHallSeatPricesRequest = async (
    tourCityId: string,
): Promise<HallSeatPriceItem[]> => {
    const query = new URLSearchParams();
    query.set("tour_city_id", tourCityId);

    return apiRequest<HallSeatPriceItem[]>(
        `/hall-prices/seat-prices?${query.toString()}`,
        {
            method: "GET",
            auth: true,
        },
    );
};

// PATCH /hall-prices/seat-prices/{seat_price_id}/count
export const updateHallSeatPriceCountRequest = async (
    seatPriceId: string,
    count: number,
): Promise<HallSeatPriceItem> => {
    const query = new URLSearchParams();
    query.set("count", String(count));

    return apiRequest<HallSeatPriceItem>(
        `/hall-prices/seat-prices/${seatPriceId}/count?${query.toString()}`,
        {
            method: "PATCH",
            auth: true,
        },
    );
};

// POST /hall-prices/seat-prices/{seat_price_id}/split
// Разделяет стоячий блок на часть, которая уйдёт в текущий заказ (count), и
// остаток вместимости, который остаётся свободным для будущих отдельных заказов.
// Возвращает строку, которую нужно передать в создание заказа.
// Бэкенд теперь возвращает СПИСОК из `count` отдельных строк (по одной на билет),
// а не одну строку с count=N — иначе в заказе создавался всего 1 билет вместо N.
export const splitStandingSeatPriceRequest = async (
    seatPriceId: string,
    count: number,
): Promise<HallSeatPriceItem[]> => {
    const query = new URLSearchParams();
    query.set("count", String(count));

    return apiRequest<HallSeatPriceItem[]>(
        `/hall-prices/seat-prices/${seatPriceId}/split?${query.toString()}`,
        {
            method: "POST",
            auth: true,
        },
    );
};


type HallSeatPricesResponse =
    | HallSeatPriceItem[]
    | {
    items?: HallSeatPriceItem[];
    data?: HallSeatPriceItem[];
};

const normalizeHallSeatPricesResponse = (
    response: HallSeatPricesResponse,
): HallSeatPriceItem[] => {
    if (Array.isArray(response)) {
        return response;
    }

    if (Array.isArray(response.items)) {
        return response.items;
    }

    if (Array.isArray(response.data)) {
        return response.data;
    }

    return [];
};

// POST /hall-prices/seat-prices/bulk
export const bulkUpsertHallSeatPricesRequest = async (
    payload: { items: HallSeatPriceBulkItem[] },
): Promise<HallSeatPriceItem[]> => {
    const response = await apiRequest<HallSeatPricesResponse>("/hall-prices/seat-prices/bulk",         {
            method: "POST",
            body: JSON.stringify(payload),
            headers: {
                "Content-Type": "application/json",
            },
            auth: true,
        },
    );

    return normalizeHallSeatPricesResponse(response);
};

export const importTourCitySeatPricesXlsxRequest = async (
    tourCityId: string,
    file: File,
): Promise<unknown> => {
    const formData = new FormData();

    formData.append("file", file);
    formData.append("colors", "");

    return apiRequest<unknown>(`/import/tour-city/${tourCityId}/seats-prices-xlsx`, {
        method: "POST",
        auth: true,
        body: formData,
    });
};
